import { computed, effect, EventEmitter, Inject, Injectable, signal } from "@angular/core";
import { LayerService, Region } from "../services/layer.service";
import { isPointInPolygon } from "../utils";
import { TransformationService } from "../services/transformation.service";
import { OfflineModeService } from "../../services/offline-mode.service";
import { PROJECT_PROVIDER, DATA_PROVIDER, DataProvider, FILTER_PROVIDER } from "../../core";

export interface BoundsSelection {
	pointIds: string[];
	active: boolean;
	filterName?: string;
	name?: string;
}

export interface ProjectPointSelection {
	boundsSelections: BoundsSelection[];
	fromRegions: number[];
	regionFilters?: Record<number, string>;
	regionNames?: Record<number, string>;
	globalFilterName?: string;
	useGlobalFilter?: boolean;
}

/**
 * Service für Projekt-Punkte-Verwaltung.
 * Lädt Punkt-IDs automatisch beim Projekt-Wechsel und stellt sie als Signals bereit.
 * Ermöglicht Karten-Highlighting auch ohne geöffnete Punkte-Section.
 */
@Injectable({ providedIn: 'root' })
export class ProjectPointsService {
	// === State Signals ===
	boundsSelections = signal<BoundsSelection[]>([]);
	activeRegionIndices = signal<number[]>([]);
	regionPointIds = signal<string[]>([]);
	filteredBoundsPointIds = signal<string[]>([]);
	filterPointIds = signal<string[]>([]);
	assignedPointIds = signal<string[]>([]);
	regionFilters = signal<Record<number, string>>({});
	regionNames = signal<Record<number, string>>({});
	globalFilterName = signal<string | null>(null);
	useGlobalFilter = signal<boolean>(false);
	pointsDownloadStatus = signal<'none' | 'complete'>('none');

	// === Loading State ===
	isLoading = signal<boolean>(false);
	isInitialized = signal<boolean>(false);

	// Cache für queryIndex() um mehrfache DB-Anfragen zu vermeiden
	private punkteCache: { rows: any[] } | null = null;
	private punkteCacheVersion = 0;

	// Version für Projektwechsel-Tracking (verhindert Race-Conditions)
	private loadVersion = 0;

	// Event wenn Projekt-Punkte geladen wurden
	loaded = new EventEmitter<void>();

	// === Computed Signals ===
	boundsCount = computed(() => this.filteredBoundsPointIds().length);
	regionCount = computed(() => this.regionPointIds().length);
	filterCount = computed(() => this.filterPointIds().length);
	assignedCount = computed(() => this.assignedPointIds().length);
	hasActiveRegions = computed(() => this.activeRegionIndices().length > 0);
	regionsCount = computed(() => this.ls.regions()?.regions?.length ?? 0);

	regionsIndeterminate = computed(() => {
		const active = this.activeRegionIndices().length;
		const total = this.regionsCount();
		return active > 0 && active < total;
	});

	hasFilterOnly = computed(() => {
		const hasFilter = !!this.globalFilterName() && this.useGlobalFilter();
		const hasSelections = this.boundsSelections().some(s => s.active) || this.hasActiveRegions();
		return hasFilter && !hasSelections;
	});

	allProjectPointIds = computed(() => {
		const set = new Set<string>();
		this.filteredBoundsPointIds().forEach(id => set.add(id));
		this.regionPointIds().forEach(id => set.add(id));
		this.assignedPointIds().forEach(id => set.add(id));
		if (this.hasFilterOnly()) {
			this.filterPointIds().forEach(id => set.add(id));
		}
		return set;
	});

	totalCount = computed(() => this.allProjectPointIds().size);

	constructor(
		@Inject(PROJECT_PROVIDER) private ps: any,
		@Inject(DATA_PROVIDER) private ds: DataProvider,
		private ls: LayerService,
		@Inject(FILTER_PROVIDER) private fs: any,
		private ts: TransformationService,
		private oms: OfflineModeService
	) {
		// Bei Projekt-Wechsel: Cache invalidieren und Daten laden oder zurücksetzen
		effect(() => {
			const projectId = this.ps.project();
			// Version erhöhen um laufende Lade-Operationen zu invalidieren
			const currentVersion = ++this.loadVersion;
			this.invalidatePunkteCache();
			if (projectId) {
				// Warten bis DataService initialisiert ist, um Race-Conditions
				// mit IndexedDB-Verbindungen beim Projektwechsel zu vermeiden
				this.ds.waitForInitialization().then(() => {
					// Nur laden wenn keine neuere Version gestartet wurde
					if (this.loadVersion === currentVersion) {
						this.loadProjectPoints();
					}
				});
			} else {
				this.reset();
			}
		});

		// Auf Bounds-Selektion reagieren
		this.ls.pointsSelectedFromBounds.subscribe(async (ids: string[]) => {
			if (ids.length === 0) return;
			const newSelection: BoundsSelection = { pointIds: ids, active: true };
			this.boundsSelections.set([...this.boundsSelections(), newSelection]);
			await this.updateFilteredBoundsPointIds();
			this.saveSelection();
		});

		// Bei Regions-Änderungen: Ungültige Indices bereinigen und Punkte neu berechnen
		effect(() => {
			const regions = this.ls.regions()?.regions;
			if (regions !== undefined && this.isInitialized()) {
				// Nur validieren wenn Regions geladen sind (length > 0).
				// Bei leerem Array (Initialwert oder noch nicht geladen) keine
				// Indices bereinigen, da sonst eine Race-Condition die aktiven
				// Bereiche löscht bevor sie aus dem Storage geladen wurden.
				if (regions.length > 0) {
					const validIndices = this.activeRegionIndices().filter(i => i < regions.length);
					if (validIndices.length !== this.activeRegionIndices().length) {
						this.activeRegionIndices.set(validIndices);
						const currentFilters = this.regionFilters();
						const currentNames = this.regionNames();
						const cleanedFilters: Record<number, string> = {};
						const cleanedNames: Record<number, string> = {};
						for (const idx of validIndices) {
							if (currentFilters[idx]) cleanedFilters[idx] = currentFilters[idx];
							if (currentNames[idx]) cleanedNames[idx] = currentNames[idx];
						}
						this.regionFilters.set(cleanedFilters);
						this.regionNames.set(cleanedNames);
						if (this.ps.project()) {
							this.saveSelection();
						}
					}
				}
				this.updateRegionPoints();
			}
		});

		// Download-Status aktualisieren
		this.ds.pointsDownloadCompleted.subscribe(() => this.updatePointsDownloadStatus());
		this.ds.downloadedPointsDiscarded.subscribe(() => this.updatePointsDownloadStatus());

		// Bei Punkt-Speicherung: Zugewiesene Punkte aktualisieren und Cache invalidieren
		this.ds.pointSaved.subscribe(() => {
			this.invalidatePunkteCache();
			this.onPointSaved();
		});

		// Bei Filter-Änderung
		this.fs.apply.subscribe(() => this.onFilterApplied());
		this.fs.filtersUpdated.subscribe(() => this.onFilterApplied());

		// Cache invalidieren bei Datenänderungen
		this.ds.checkedIn.subscribe(() => this.invalidatePunkteCache());
		this.ds.checkedOut.subscribe(() => this.invalidatePunkteCache());
		this.ds.discarded.subscribe(() => this.invalidatePunkteCache());
		this.ds.pointsRefreshRequested.subscribe(() => this.invalidatePunkteCache());
	}

	// === Cache-Management ===

	/** Invalidiert den Punkte-Cache (bei Datenänderungen) */
	invalidatePunkteCache(): void {
		this.punkteCache = null;
		this.punkteCacheVersion++;
	}

	/** Holt Punkte aus Cache oder lädt sie aus der DB */
	private async getCachedPunkte(): Promise<{ rows: any[] }> {
		if (this.punkteCache) {
			return this.punkteCache;
		}

		const response: any = await this.ds.queryIndex();
		this.punkteCache = { rows: response?.rows ?? [] };
		return this.punkteCache;
	}

	// === Laden ===

	async loadProjectPoints(): Promise<void> {
		this.isLoading.set(true);
		try {
			// Warten bis DataService initialisiert ist, um Race-Conditions
			// mit IndexedDB-Verbindungen zu vermeiden
			await this.ds.waitForInitialization();

			// 1. Gespeicherte Auswahl laden
			const saved = await this.ps.getProjectValue('projectPoints') as ProjectPointSelection | null;
			if (saved) {
				// Migration: alte fromBounds zu neuer Struktur
				if ((saved as any).fromBounds && !saved.boundsSelections) {
					const oldBounds = (saved as any).fromBounds as string[];
					if (oldBounds.length > 0) {
						this.boundsSelections.set([{ pointIds: oldBounds, active: true }]);
					}
				} else {
					this.boundsSelections.set(saved.boundsSelections ?? []);
				}
				this.activeRegionIndices.set(saved.fromRegions ?? []);
				this.regionFilters.set(saved.regionFilters ?? {});
				this.regionNames.set(saved.regionNames ?? {});
				this.globalFilterName.set(saved.globalFilterName ?? null);
				this.useGlobalFilter.set(saved.useGlobalFilter ?? false);
			}

			// 2. Punkt-IDs parallel berechnen
			await Promise.all([
				this.updateRegionPoints(),
				this.updateFilteredBoundsPointIds(),
				this.updateFilterPointIds(),
				this.updateAssignedPoints()
			]);

			// 3. Status aktualisieren
			this.updatePointsDownloadStatus();
			this.isInitialized.set(true);

			// 4. Events emittieren
			this.emitProjectPoints();
			this.emitAssignedPoints();
			this.loaded.emit();
		} finally {
			this.isLoading.set(false);
		}
	}

	private reset() {
		this.boundsSelections.set([]);
		this.activeRegionIndices.set([]);
		this.regionPointIds.set([]);
		this.filteredBoundsPointIds.set([]);
		this.filterPointIds.set([]);
		this.assignedPointIds.set([]);
		this.regionFilters.set({});
		this.regionNames.set({});
		this.globalFilterName.set(null);
		this.useGlobalFilter.set(false);
		this.pointsDownloadStatus.set('none');
		this.isInitialized.set(false);
		// Events mit leeren Sets emittieren
		this.emitProjectPoints();
		this.emitAssignedPoints();
	}

	// === Update-Methoden ===

	async updateRegionPoints(): Promise<void> {
		const regions = this.ls.regions()?.regions ?? [];
		const activeIndices = this.activeRegionIndices();
		const savedFilters = this.regionFilters();

		if (regions.length === 0 || activeIndices.length === 0) {
			this.regionPointIds.set([]);
			this.emitProjectPoints();
			return;
		}

		const response = await this.getCachedPunkte();
		const rows = response.rows;
		const inRegionIds: string[] = [];

		for (const i of activeIndices) {
			if (i >= regions.length) continue;
			const region = regions[i];

			const savedFilter = savedFilters[i] ?? (savedFilters as any)[String(i)];
			const filterName = savedFilter ?? (this.useGlobalFilter() ? this.globalFilterName() : null);
			const filterFunc = filterName ? await this.getFilterFunction(filterName) : null;

			rows.forEach((row: any) => {
				const header = row.key[4];
				if (header?.koordinaten) {
					const ll = this.ts.from(header.koordinaten);
					// Prüfen ob Punkt in Region liegt
					let isInRegion = false;
					if (region.type === 'polygon' && region.polygon) {
						// Polygon: Erst Bounding-Box-Check (schnell), dann Polygon-Check (genau)
						if (region.bounds.contains(ll)) {
							isInRegion = isPointInPolygon(ll, region.polygon);
						}
					} else {
						// Rechteck: Einfacher Bounds-Check
						isInRegion = region.bounds.contains(ll);
					}

					if (isInRegion) {
						if (filterFunc) {
							const doc = {
								_id: row.id,
								type: row.key[1],
								header: header,
								state: row.key[5],
								version: row.key[6]
							};
							try {
								if (filterFunc(doc)) {
									inRegionIds.push(row.id);
								}
							} catch {
								// Filter error - exclude point
							}
						} else {
							inRegionIds.push(row.id);
						}
					}
				}
			});
		}

		this.regionPointIds.set(inRegionIds);
		this.emitProjectPoints();
	}

	async updateFilteredBoundsPointIds(): Promise<void> {
		const activeSelections = this.boundsSelections().filter(s => s.active);
		if (activeSelections.length === 0) {
			this.filteredBoundsPointIds.set([]);
			return;
		}

		const response = await this.getCachedPunkte();
		const rows = response.rows;
		const rowsById = new Map<string, any>();
		rows.forEach((row: any) => rowsById.set(row.id, row));

		const result: string[] = [];

		for (const sel of activeSelections) {
			const filterName = sel.filterName ?? (this.useGlobalFilter() ? this.globalFilterName() : null);
			const filterFunc = filterName ? await this.getFilterFunction(filterName) : null;

			for (const id of sel.pointIds) {
				if (filterFunc) {
					const row = rowsById.get(id);
					if (row) {
						const header = row.key[4];
						const doc = {
							_id: row.id,
							type: row.key[1],
							header: header,
							state: row.key[5],
							version: row.key[6]
						};
						try {
							if (filterFunc(doc)) {
								result.push(id);
							}
						} catch {
							// Filter error - exclude point
						}
					}
				} else {
					result.push(id);
				}
			}
		}

		this.filteredBoundsPointIds.set(result);
	}

	async updateFilterPointIds(): Promise<void> {
		const globalFilter = this.globalFilterName();
		const useFilter = this.useGlobalFilter();
		const hasSelections = this.boundsSelections().some(s => s.active) || this.hasActiveRegions();

		if (!globalFilter || !useFilter || hasSelections) {
			this.filterPointIds.set([]);
			return;
		}

		const filteredIds = [...this.fs.filteredDocIds];
		this.filterPointIds.set(filteredIds);
		this.emitProjectPoints();
	}

	async updateAssignedPoints(): Promise<void> {
		const projectId = this.ps.project();
		if (!projectId) {
			this.assignedPointIds.set([]);
			return;
		}

		// View-Keys nutzen statt include_docs (header ist key[4], enthält projects)
		const response = await this.getCachedPunkte();
		const rows = response.rows;

		const assignedIds = rows
			.filter((row: any) => row.key?.[4]?.projects?.some((p: any) => p.id === projectId))
			.map((row: any) => row.id);

		this.assignedPointIds.set(assignedIds);
	}

	updatePointsDownloadStatus(): void {
		this.pointsDownloadStatus.set(this.ds.arePointsDownloaded() ? 'complete' : 'none');
	}

	// === Actions ===

	toggleRegionPoints(enabled: boolean): void {
		if (enabled) {
			const count = this.regionsCount();
			this.activeRegionIndices.set(Array.from({ length: count }, (_, i) => i));
		} else {
			this.activeRegionIndices.set([]);
		}
		this.updateRegionPoints();
		this.saveSelection();
	}

	async saveSelection(hasChanged = true): Promise<void> {
		const selection: ProjectPointSelection = {
			boundsSelections: this.boundsSelections(),
			fromRegions: this.activeRegionIndices(),
			regionFilters: this.regionFilters(),
			regionNames: this.regionNames(),
			globalFilterName: this.globalFilterName() ?? undefined,
			useGlobalFilter: this.useGlobalFilter()
		};
		await this.ps.setProjectValue('projectPoints', selection);

		await this.updateFilterPointIds();
		this.emitProjectPoints();

		if (hasChanged && !this.oms.offlineMode() && this.ds.arePointsDownloaded()) {
			await this.ds.discardDownloadedPoints();
		}
	}

	// === Gefilterte Punkt-IDs ===

	async getFilteredProjectPointIds(): Promise<string[]> {
		await this.updateFilteredBoundsPointIds();
		return [...this.allProjectPointIds()];
	}

	// === Helpers ===

	private async getFilterFunction(filterName: string): Promise<((doc: any) => boolean) | null> {
		const condExp = await this.fs.get(filterName);
		if (!condExp) return null;

		const funcStr = condExp.filterFunction(false, false, []);
		let filterFunc: (doc: any) => boolean;
		eval(`filterFunc = ${funcStr}`);
		return filterFunc!;
	}

	private async onFilterApplied(): Promise<void> {
		const saved = await this.ps.getProjectValue('projectPoints') as ProjectPointSelection | null;
		if (saved) {
			this.globalFilterName.set(saved.globalFilterName ?? null);
			this.useGlobalFilter.set(saved.useGlobalFilter ?? false);
		}
		await this.updateFilteredBoundsPointIds();
		await this.updateRegionPoints();
		await this.updateFilterPointIds();
		this.updateFilterServiceIds();
	}

	private updateFilterServiceIds(): void {
		const globalFilter = this.globalFilterName();
		if (globalFilter && this.useGlobalFilter()) {
			// Projekt-globaler Filter: IDs vom Projekt-Service setzen
			this.fs.setFilteredDocIds(this.allProjectPointIds());
		}
		// Wenn kein Projekt-globaler Filter aktiv ist, lassen wir die filteredDocIds
		// vom normalen Filter-Mechanismus verwalten (nicht löschen!)
	}

	private async onPointSaved(): Promise<void> {
		await this.updateAssignedPoints();
		this.emitProjectPoints();
		this.emitAssignedPoints();
	}

	private emitProjectPoints(): void {
		this.ls.projectPointsChanged.emit(this.allProjectPointIds());
	}

	private emitAssignedPoints(): void {
		this.ls.assignedPointsChanged.emit(new Set(this.assignedPointIds()));
	}

	serializeSelection(): string {
		return JSON.stringify({
			boundsSelections: this.boundsSelections(),
			fromRegions: this.activeRegionIndices(),
			regionFilters: this.regionFilters(),
			regionNames: this.regionNames(),
			globalFilterName: this.globalFilterName(),
			useGlobalFilter: this.useGlobalFilter()
		});
	}

	/** Prüft ob ein Punkt dem Projekt zugeordnet ist */
	isAssigned(pointId: string): boolean {
		return this.assignedPointIds().includes(pointId);
	}
}
